import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHeader } from "@/client/components/page-header";
import { apiQuery, useApiSuspenseQuery } from "@/client/lib/api";
import { ensurePage, pageHead } from "@/client/lib/page";
import { FunnelPanel } from "@/client/modules/funil/funnel-panel";
import Loading from "@/client/modules/radar/loading";
import {
  FaltasByMonthPanel,
  KpiTile,
  NoShowPanel,
  QuotesPanel,
  VolumePanel,
} from "@/client/modules/radar/radar-panels";
import { cn } from "@/shared/utils";

type Search = { dias?: string };

export const Route = createFileRoute("/app/radar")({
  validateSearch: (s: Record<string, unknown>): Search => ({ dias: s.dias as string | undefined }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => ensurePage(context.queryClient, apiQuery("radar", "page", deps)),
  head: () => pageHead("Radar de gestão"),
  pendingComponent: Loading,
  component: RadarPage,
});

const LABELS: Record<number, string> = { 30: "30 dias", 90: "90 dias", 180: "6 meses", 365: "1 ano" };

function RadarPage() {
  const search = Route.useSearch();
  const { data } = useApiSuspenseQuery("radar", "page", search);
  const { summary } = data;
  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6">
      <PageHeader
        title="Radar de gestão"
        description="Como a clínica está operando — presença, agenda e captação. Só leitura."
      />
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500">Período:</span>
        <div className="flex gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-soft">
          {data.periods.map((p) => (
            <Link
              key={p}
              to="/app/radar"
              search={{ dias: String(p) }}
              resetScroll={false}
              className={cn(
                "rounded-lg px-3 py-1.5 text-xs font-medium transition",
                data.dias === p ? "bg-brand-700 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100",
              )}
            >
              {LABELS[p]}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiTile
          label="Taxa de falta"
          value={summary.taxaFaltaPct === null ? "—" : `${summary.taxaFaltaPct}%`}
          hint="faltas ÷ (vieram + faltaram)"
          tone={
            summary.taxaFaltaPct === null
              ? "neutral"
              : summary.taxaFaltaPct >= 20
                ? "bad"
                : summary.taxaFaltaPct >= 10
                  ? "warn"
                  : "good"
          }
        />
        <KpiTile
          label="Comparecimento"
          value={summary.taxaComparecimentoPct === null ? "—" : `${summary.taxaComparecimentoPct}%`}
          hint="vieram ÷ marcados resolvidos"
          tone="neutral"
        />
        <KpiTile label="Atendidos" value={String(summary.atendidos)} hint="no período" tone="neutral" />
        <KpiTile
          label="Faltas"
          value={String(summary.faltas)}
          hint={summary.cancelados > 0 ? `+ ${summary.cancelados} cancelados` : "no período"}
          tone={summary.faltas > 0 ? "warn" : "neutral"}
        />
      </div>

      {summary.semDesfecho > 0 && (
        <p className="-mt-2 text-xs text-slate-400">
          {summary.semDesfecho} compromisso{summary.semDesfecho === 1 ? "" : "s"} do período seguem como “agendado” sem
          baixa (não entram na taxa de falta). Marcar como atendido/faltou na Agenda deixa o radar mais preciso.
        </p>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <FaltasByMonthPanel data={data.faltasMes} />
        <VolumePanel
          title="Movimento da agenda"
          subtitle="Compromissos por semana (últimas 8 semanas)."
          data={data.volumeSemana}
          barClass="bg-brand-400"
          unit="compromissos"
        />
        <VolumePanel
          title="Novos pacientes"
          subtitle="Cadastros criados por mês (últimos 6 meses)."
          data={data.novosPacientes}
          barClass="bg-sky-400"
          unit="cadastros"
        />
        {data.showQuotes && <QuotesPanel stats={data.quoteStats} />}
      </div>
      {data.showFunil && data.funnelStats && <FunnelPanel stats={data.funnelStats} valorOn={data.valorOn} />}
      <NoShowPanel rows={data.noShows} />
    </div>
  );
}
